import { useId, useMemo, useState } from 'react'
import type { PlantMapTile } from '../api/types'
import { plantMapLayout, rankAreas } from '../features/dashboard/models'
import { areaCode, areaLabel, areaTitle } from '../lib/areas'
import { BAND_ORDER, bandMeta, formatScore } from '../lib/bands'
import { Icon } from './Icon'
import { BandShapeIcon, cx } from './ui'

function MixBar({ tile }: { tile: PlantMapTile }) {
  if (tile.total === 0) return null
  return (
    <span className="mixbar" aria-hidden="true">
      {BAND_ORDER.map((b) => {
        const n = tile.counts[b]
        return n ? <span key={b} className={cx('mixbar__seg', b === 'not_assessed' && 'mixbar__seg--na')} style={{ flexGrow: n, background: b === 'not_assessed' ? undefined : bandMeta(b).color }} /> : null
      })}
    </span>
  )
}

function Tile({ tile, selected, onSelect }: { tile: PlantMapTile; selected: boolean; onSelect: (area: number | undefined) => void }) {
  const meta = bandMeta(tile.worst_band)
  const name = areaLabel(tile.code, tile.name)
  return (
    <button
      type="button"
      className={cx('pm-tile', selected && 'is-selected')}
      data-area={tile.code}
      data-band={meta.code}
      aria-pressed={selected}
      aria-label={`${name} (${areaCode(tile)}): ${tile.total === 0 ? 'sin equipos' : `peor banda: ${meta.label}`}`}
      title={areaTitle(tile)}
      onClick={() => onSelect(selected ? undefined : tile.area)}
      style={{ ['--tile-color' as string]: meta.color, ['--tile-soft' as string]: meta.soft, ['--tile-ink' as string]: meta.ink }}
    >
      <span className="pm-tile__code mono">{areaCode(tile)}</span>
      <span className="pm-tile__name">{name}</span>
      <span className="pm-tile__band"><BandShapeIcon shape={meta.shape} size={13} />{tile.total === 0 ? 'Sin equipos' : meta.label}</span>
      <span className="pm-tile__meta mono">{tile.total === 0 ? '' : `${tile.total} eq. · prom. ${formatScore(tile.avg_score)}`}</span>
      <MixBar tile={tile} />
    </button>
  )
}

export function PlantMap({ tiles, selectedArea, onSelect }: { tiles: PlantMapTile[]; selectedArea?: number; onSelect: (area: number | undefined) => void }) {
  const [search, setSearch] = useState('')
  const searchId = useId()
  const layout = useMemo(() => plantMapLayout(tiles), [tiles])
  const ranked = useMemo(() => rankAreas(tiles, search), [tiles, search])
  const atRisk = (t: PlantMapTile) => t.counts.critical + t.counts.preventive
  const maxRisk = Math.max(1, ...ranked.map((t) => (t.total ? atRisk(t) / t.total : 0)))

  return (
    <div className="plantmap">
      <div className="plantmap__schematic">
        <div role="group" aria-label="Flujo del proceso de la planta" className="pm-flow" style={{ ['--cols' as string]: layout.main.length }}>
          {layout.main.map((t, i) => (
            <div key={t.code} className="pm-cell" style={{ gridColumn: i + 1, gridRow: 1 }}>
              <Tile tile={t} selected={selectedArea === t.area} onSelect={onSelect} />
              {i < layout.main.length - 1 ? <Icon name="chevron-right" className="pm-arrow" size={18} /> : null}
            </div>
          ))}
          {layout.branches.map(({ tile, parentIndex }) => (
            <div key={tile.code} className="pm-cell pm-cell--branch" style={{ gridColumn: parentIndex + 1, gridRow: 2 }}>
              <span className="pm-branch-link" aria-hidden="true" />
              <Tile tile={tile} selected={selectedArea === tile.area} onSelect={onSelect} />
              <span className="pm-branch-label">Derivación después de {areaLabel(layout.main[parentIndex]?.code, layout.main[parentIndex]?.name)}</span>
            </div>
          ))}
          <div className="pm-utilities" style={{ gridColumn: '1 / -1', gridRow: 3 }}>
            <span className="pm-utilities__label">Servicios auxiliares</span>
            {layout.utilities.map((t) => <Tile key={t.code} tile={t} selected={selectedArea === t.area} onSelect={onSelect} />)}
          </div>
        </div>
        <p className="plantmap__hint faint">Seleccionar un área para filtrar el panel. Cada recuadro muestra la peor banda de sus equipos.</p>
      </div>

      <div className="plantmap__ranking">
        <div className="plantmap__rank-head">
          <h3>Áreas por severidad <span className="muted">{ranked.length} de {tiles.length}</span></h3>
          <div className="searchbox">
            <Icon name="search" size={16} />
            <input id={searchId} type="search" role="searchbox" aria-label="Buscar áreas" placeholder="Buscar área" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        {ranked.length === 0 ? <p className="muted">Ningún área coincide con “{search}”.</p> : (
          <ul aria-label="Áreas ordenadas por severidad" className="rank-list">
            <li className="rank-list__head" aria-hidden="true"><span>Área</span><span /><span className="align-right">En riesgo</span><span className="align-right">%</span></li>
            {ranked.map((t) => {
              const risk = atRisk(t)
              const pct = t.total ? Math.round((100 * risk) / t.total) : 0
              const meta = bandMeta(t.worst_band)
              return (
                <li key={t.code} data-area={t.code}>
                  <button type="button" className={cx('rank-row', selectedArea === t.area && 'is-selected')} onClick={() => onSelect(selectedArea === t.area ? undefined : t.area)}>
                    <span className="rank-row__name"><span style={{ color: meta.color, display: 'inline-flex' }}><BandShapeIcon shape={meta.shape} size={12} /></span>{areaLabel(t.code, t.name)}</span>
                    <span className="rank-row__bar" aria-hidden="true"><span style={{ width: `${t.total ? Math.max(4, (100 * (risk / t.total)) / maxRisk) : 0}%`, background: meta.color }} /></span>
                    <span className="rank-row__n mono">{t.total ? `${risk} de ${t.total}` : '—'}</span>
                    <span className="rank-row__pct mono">{t.total ? `${pct}%` : '—'}</span>
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </div>
  )
}
